import React from 'react';
import { Box, Container, Grid, Paper, Typography, Chip } from '@mui/material';
import {
  Speed,
  Description,
  People,
  Support,
  SmartToy,
} from '@mui/icons-material';
import { brandColors } from '../../theme';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  comingSoon?: boolean;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, comingSoon }) => (
  <Paper
    sx={{
      p: 4,
      height: '100%',
      backgroundColor: brandColors.white,
      border: `1px solid ${brandColors.lightGray}`,
      borderRadius: 2,
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'visible',
      '&:hover': {
        borderColor: brandColors.blue,
        boxShadow: '0 4px 16px rgba(32, 76, 229, 0.1)',
        '& .feature-icon': {
          backgroundColor: brandColors.blue,
          color: brandColors.white,
        },
      },
    }}
  >
    {comingSoon && (
      <Chip
        label="Coming Soon"
        size="small"
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          backgroundColor: brandColors.gold,
          color: brandColors.white,
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 24,
        }}
      />
    )}
    <Box
      className="feature-icon"
      sx={{
        display: 'inline-flex',
        p: 2,
        borderRadius: 2,
        backgroundColor: brandColors.lightGray,
        mb: 3,
        transition: 'all 0.3s ease',
      }}
    >
      {React.cloneElement(icon as React.ReactElement<any>, {
        sx: { fontSize: 32, color: brandColors.indigo }
      })}
    </Box>
    
    <Typography
      variant="h6"
      sx={{
        color: brandColors.indigo,
        fontWeight: 600,
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    
    <Typography
      variant="body2"
      sx={{
        color: brandColors.mediumGray,
        lineHeight: 1.6,
      }}
    >
      {description}
    </Typography>
  </Paper>
);

export const SecondaryFeatures: React.FC = () => {
  const features = [
    {
      icon: <Speed />,
      title: 'Instant Access',
      description: 'Download reports immediately after purchase',
      comingSoon: false,
    },
    {
      icon: <Description />,
      title: 'Multiple Formats',
      description: 'PDF, Excel data tables, and online access',
      comingSoon: true,
    },
    {
      icon: <People />,
      title: 'Team Sharing',
      description: 'Collaborate with unlimited team members',
      comingSoon: true,
    },
    {
      icon: <Support />,
      title: 'Expert Support',
      description: 'Direct access to our analyst team',
      comingSoon: true,
    },
    {
      icon: <SmartToy />,
      title: 'AI Report Analyst',
      description: 'Ask questions and get instant insights from your reports',
      comingSoon: true,
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: brandColors.white,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h4"
            sx={{
              color: brandColors.indigo,
              fontWeight: 600,
              mb: 2,
            }}
          >
            Why Choose MarketCompass
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              color: brandColors.mediumGray,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Premium features designed to support your market intelligence needs
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.slice(0, 3).map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <FeatureCard
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                comingSoon={feature.comingSoon}
              />
            </Grid>
          ))}
        </Grid>
        
        <Grid container spacing={3} sx={{ mt: 0, justifyContent: 'center' }}>
          {features.slice(3).map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index + 3}>
              <FeatureCard
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                comingSoon={feature.comingSoon}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};